import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Header.module.css";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [expandedMobileLink, setExpandedMobileLink] = useState(null);

  const navLinksData = [
    {
      path: "/features",
      label: "Asset Management",
      hoverInfo: {
        title: "Asset Management",
        items: [
          "• Asset Registration",
          "• Asset Types & Categories",
          "• Ownership Management",
          "• Warranty & AMC",
          "• Depreciation",
          "• Asset Status",
        ],
      },
    },
    {
      path: "/solutions",
      label: "Tracking & Accountability",
      hoverInfo: {
        title: "Tracking & Accountability",
        items: [
          "• Asset Assignment",
          "• User & Custodian Management",
          "• Location & Department Mapping",
          "• Asset Movement",
          "• Re-Assignment",
          "• Complete Asset History",
        ],
      },
    },
    {
      path: "/pricing",
      label: "Lifecycle & Maintenance",
      hoverInfo: {
        title: "Lifecycle & Maintenance",
        items: [
          "• Procurement to Retirement",
          "• Support & Issue Management",
          "• Maintenance History",
          "• Service Tracking",
          "• Spare Parts & Consumables",
          "• Asset Lifecycle Tracking",
        ],
      },
    },
    {
      path: "/about",
      label: "Audit & Inventory",
      hoverInfo: {
        title: "Audit & Inventory",
        items: [
          "• Asset Audits",
          "• Verification",
          "• Missing & Unverified Assets",
          "• Audit History",
          "• Stock Management",
          "• Reorder Levels",
          "• Inventory Tracking",
        ],
      },
    },
    {
      path: "/reports",
      label: "Reports & Control",
      hoverInfo: {
        title: "Reports & Control",
        items: [
          "• Asset Registry Reports",
          "• Movement & Disposal Reports",
          "• Depreciation Reports",
          "• Stock & Expiry Reports",
          "• Drill-Down Filters",
          "• Management Insights",
        ],
      },
    },
    {
      path: "/contact",
      label: "Contact",
      hoverInfo: {
        title: "Connect With Our Team",
        items: [
          "• Schedule a Live Demonstration",
          "• Implementation and Training Services",
          "• Enterprise API and Custom Workflows",
          "• Dedicated Support: +91 6360919373",
          "• Submit Feature Requests or Feedback",
          "• Explore Partnership Opportunities",
        ],
      },
    },
  ];

  // Close mobile menu when clicking outside or on escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setExpandedMobileLink(null);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setExpandedMobileLink(null);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedMobileLink(null);
  };

  const toggleMobileExpansion = (index) => {
    setExpandedMobileLink(expandedMobileLink === index ? null : index);
  };

  const handleOverlayClick = () => {
    closeMobileMenu();
  };

  return (
    <header className={styles.header} role="banner">
      <div className={styles.headerContainer}>
        {/* Logo */}
        <div className={styles.logo}>
          <Link to="/" className={styles.logoLink} aria-label="Assetra Home">
            <img
              src="/logo.png"
              alt=" - Asset Management Software"
              className={styles.logoImage}
            />
          </Link>
        </div>

        {/* Desktop navigation – center */}
        <nav className={styles.navigation} aria-label="Main navigation">
          <div className={styles.navLinks}>
            {navLinksData.map((linkData, index) => (
              <div
                key={index}
                className={styles.navLinkContainer}
                onMouseEnter={() => setHoveredLink(index)}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <Link to={linkData.path} className={styles.navLink}>
                  {linkData.label}
                  <span className={styles.dropdownIcon}>▼</span>
                </Link>

                {hoveredLink === index && (
                  <div className={styles.hoverCard} role="tooltip">
                    <h4 className={styles.hoverCardTitle}>
                      {linkData.hoverInfo.title}
                    </h4>
                    <ul className={styles.hoverCardList}>
                      {linkData.hoverInfo.items.map((item, i) => (
                        <li key={i} className={styles.hoverCardItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Desktop auth buttons – right */}
        <div className={styles.authButtons}>
          <Link to="/admin-signup" className={styles.getStartedBtn}>
            Get Started
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className={styles.mobileMenuBtn}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobileMenu"
        >
          <span className={`${styles.hamburgerIcon} ${isMobileMenuOpen ? styles.hamburgerOpen : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile slide-down menu */}
      {isMobileMenuOpen && (
        <div 
          className={styles.mobileMenu}
          id="mobileMenu"
        >
          <div className={styles.mobileMenuContent}>
            <div className={styles.mobileNavLinks}>
              {navLinksData.map((linkData, index) => (
                <div key={index} className={styles.mobileNavLinkContainer}>
                  <div className={styles.mobileNavLinkWrapper}>
                    <Link
                      to={linkData.path}
                      className={styles.mobileNavLink}
                      onClick={closeMobileMenu}
                      role="menuitem"
                    >
                      {linkData.label}
                    </Link>
                    <button
                      type="button"
                      className={styles.mobileDropdownBtn}
                      onClick={() => toggleMobileExpansion(index)}
                      aria-label={`Toggle ${linkData.label} details`}
                    >
                      <span className={`${styles.mobileDropdownIcon} ${expandedMobileLink === index ? styles.mobileDropdownIconOpen : ''}`}>
                        ▼
                      </span>
                    </button>
                  </div>
                  
                  {expandedMobileLink === index && (
                    <div className={styles.mobileExpandedContent}>
                      <h4 className={styles.mobileExpandedTitle}>
                        {linkData.hoverInfo.title}
                      </h4>
                      <ul className={styles.mobileExpandedList}>
                        {linkData.hoverInfo.items.map((item, i) => (
                          <li key={i} className={styles.mobileExpandedItem}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.mobileAuthButtons}>
              <Link
                to="/admin-signup"
                className={styles.mobileGetStartedBtn}
                onClick={closeMobileMenu}
                role="menuitem"
              >
                Get Started
              </Link>
            </div>

            <div className={styles.mobileSalesInfo}>
              <span>Support: +91 6360919373</span>
            </div>
          </div>
        </div>
      )}

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div 
          className={styles.mobileMenuOverlay} 
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header;